import { INumberIdEntity, IStringIdEntity } from "../base.interface";
import { ICompany } from "./company";
import { IGroup } from "../group.interface";

type ValueOf<T> = T[keyof T];

export const SponsorshipPolicyStatuses = Object.freeze({
  ACTIVE: "active",
  INACTIVE: "inactive",
});
export type SponsorshipPolicyStatusValues = ValueOf<typeof SponsorshipPolicyStatuses>;

export const CoverageComponentTypes = Object.freeze({
  ENERGY: "ENERGY",
  TIME: "TIME",
  PARKING_TIME: "PARKING_TIME",
  FLAT: "FLAT",
  RESERVATION: "RESERVATION",
});
export type CoverageComponentTypeValues = ValueOf<typeof CoverageComponentTypes>;

export const CoverageTypes = Object.freeze({
  FULL: "FULL",
  PERCENT: "PERCENT",
  FIXED_DISCOUNT: "FIXED_DISCOUNT",
  NONE: "NONE",
});
export type CoverageTypeValues = ValueOf<typeof CoverageTypes>;

export const CoverageUnits = Object.freeze({
  PERCENT: "percent",
  EUR: "eur",
  EUR_PER_KWH: "eur_per_kwh",
  EUR_PER_MIN: "eur_per_min",
});
export type CoverageUnitValues = ValueOf<typeof CoverageUnits>;

export const CoverageLimitScopes = Object.freeze({
  PER_USER: "PER_USER",
  PER_GROUP: "PER_GROUP",
  PER_SPONSOR_COMPANY: "PER_SPONSOR_COMPANY",
});
export type CoverageLimitScopeValues = ValueOf<typeof CoverageLimitScopes>;

export const CoverageLimitPeriods = Object.freeze({
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
});
export type CoverageLimitPeriodValues = ValueOf<typeof CoverageLimitPeriods>;

export const CoverageLimitMetrics = Object.freeze({
  AMOUNT_EUR: "AMOUNT_EUR",
  ENERGY_KWH: "ENERGY_KWH",
  SESSIONS_COUNT: "SESSIONS_COUNT",
});
export type CoverageLimitMetricValues = ValueOf<typeof CoverageLimitMetrics>;

export const CoverageLimitBehaviorOnExceed = Object.freeze({
  USER_PAYS_REST: "USER_PAYS_REST",
  BLOCK: "BLOCK",
  FALLBACK_TARIFF: "FALLBACK_TARIFF",
});
export type CoverageLimitBehaviorOnExceedValues = ValueOf<typeof CoverageLimitBehaviorOnExceed>;

export const SessionAllocationDimensions = Object.freeze({
  ENERGY: "ENERGY",
  PARKING: "PARKING",
  TIME: "TIME",
  RESERVATION: "RESERVATION",
  FLAT: "FLAT",
});
export type SessionAllocationDimensionValues = ValueOf<typeof SessionAllocationDimensions>;

export const SessionAllocationPayerTypes = Object.freeze({
  USER: "USER",
  COMPANY: "COMPANY",
});
export type SessionAllocationPayerTypeValues = ValueOf<typeof SessionAllocationPayerTypes>;

export const SponsorshipScheduleDays = Object.freeze({
  MONDAY: "MONDAY",
  TUESDAY: "TUESDAY",
  WEDNESDAY: "WEDNESDAY",
  THURSDAY: "THURSDAY",
  FRIDAY: "FRIDAY",
  SATURDAY: "SATURDAY",
  SUNDAY: "SUNDAY",
});
export type SponsorshipScheduleDayValues = ValueOf<typeof SponsorshipScheduleDays>;

export const SponsorshipActivationRuleTimezones = Object.freeze({
  EUROPE_ROME: "Europe/Rome",
  EUROPE_LONDON: "Europe/London",
  EUROPE_ATHENS: "Europe/Athens",
});
export type SponsorshipActivationRuleTimezoneValues = ValueOf<typeof SponsorshipActivationRuleTimezones>;

export const SponsorshipNotificationChangeTypes = Object.freeze({
  GROUP_LINKED: "GROUP_LINKED",
  GROUP_CHANGED: "GROUP_CHANGED",
  GROUP_UNLINKED: "GROUP_UNLINKED",
  POLICY_UPDATED: "POLICY_UPDATED",
  COVERAGE_UPDATED: "COVERAGE_UPDATED",
  ACTIVATION_RULE_UPDATED: "ACTIVATION_RULE_UPDATED",
  GROUP_MEMBERSHIP_UPDATED: "GROUP_MEMBERSHIP_UPDATED",
  POLICY_DELETED: "POLICY_DELETED",
});
export type SponsorshipNotificationChangeTypeValues = ValueOf<typeof SponsorshipNotificationChangeTypes>;

export const SponsorshipCoverageNotificationTypes = Object.freeze({
  ADDED: "ADDED",
  MODIFIED: "MODIFIED",
  REMOVED: "REMOVED",
});
export type SponsorshipCoverageNotificationTypeValues = ValueOf<typeof SponsorshipCoverageNotificationTypes>;

export const SPONSORSHIP_NOTIFICATION_QUEUE_NAME = "sponsorship:notification";
export const SPONSORSHIP_NOTIFICATION_DELAY_MS = 30 * 60 * 1000;

export interface ISponsorshipNotificationQueuePayload {
  policyId: number;
  policyName?: string;
  sponsorName?: string;
  previousUserIds: string[];
  changeType: SponsorshipNotificationChangeTypeValues;
}

export interface ISponsorshipActivationRule extends INumberIdEntity {
  sponsorshipPolicyId: number;
  startDay?: SponsorshipScheduleDayValues | null;
  startTime?: string | null;
  endDay?: SponsorshipScheduleDayValues | null;
  endTime?: string | null;
  timezone?: SponsorshipActivationRuleTimezoneValues | null;
  notes?: string | null;
  sponsorshipPolicy?: ISponsorshipPolicy;
}

export interface ICreateSponsorshipActivationRuleDTO {
  sponsorshipPolicyId: number;
  startDay?: SponsorshipScheduleDayValues | null;
  startTime?: string | null;
  endDay?: SponsorshipScheduleDayValues | null;
  endTime?: string | null;
  timezone?: SponsorshipActivationRuleTimezoneValues | null;
  notes?: string | null;
}

export interface IUpdateSponsorshipActivationRuleDTO extends Partial<ICreateSponsorshipActivationRuleDTO> { }

export interface ISponsorshipPolicy extends INumberIdEntity {
  companyId: number;
  name: string;
  priority: number;
  status: SponsorshipPolicyStatusValues;
  validFrom: Date | string;
  validTo?: Date | string | null;
  appliesToGroupId?: number | null;
  contractVersionScopeId?: number | null;
  notes?: string | null;
  company?: ICompany;
  appliesToGroup?: IGroup;
  activationRules?: ISponsorshipActivationRule[];
}

export interface ICreateSponsorshipPolicyDTO {
  companyId: number;
  name: string;
  priority: number;
  status: SponsorshipPolicyStatusValues;
  validFrom: Date | string;
  validTo?: Date | string | null;
  appliesToGroupId?: number | null;
  contractVersionScopeId?: number | null;
  notes?: string | null;
}

export interface IUpdateSponsorshipPolicyDTO extends Partial<ICreateSponsorshipPolicyDTO> { }

export interface ICoverageRule extends INumberIdEntity {
  sponsorshipPolicyId: number;
  componentType: CoverageComponentTypeValues;
  coverageType: CoverageTypeValues;
  value: number;
  unit: CoverageUnitValues;
  priority?: number | null;
  notes?: string | null;
  sponsorshipPolicy?: ISponsorshipPolicy;
}

export interface ICreateCoverageRuleDTO {
  sponsorshipPolicyId: number;
  componentType: CoverageComponentTypeValues;
  coverageType: CoverageTypeValues;
  value: number;
  unit: CoverageUnitValues;
  priority?: number | null;
  notes?: string | null;
}

export interface IUpdateCoverageRuleDTO extends Partial<ICreateCoverageRuleDTO> { }

export interface ICoverageRuleLimit extends INumberIdEntity {
  sponsorshipPolicyId: number;
  limitScope: CoverageLimitScopeValues;
  period: CoverageLimitPeriodValues;
  metric: CoverageLimitMetricValues;
  limitValue: number;
  behaviorOnExceed: CoverageLimitBehaviorOnExceedValues;
  notes?: string | null;
  sponsorshipPolicy?: ISponsorshipPolicy;
}

export interface ICreateCoverageRuleLimitDTO {
  sponsorshipPolicyId: number;
  limitScope: CoverageLimitScopeValues;
  period: CoverageLimitPeriodValues;
  metric: CoverageLimitMetricValues;
  limitValue: number;
  behaviorOnExceed: CoverageLimitBehaviorOnExceedValues;
  notes?: string | null;
}

export interface IUpdateCoverageRuleLimitDTO extends Partial<ICreateCoverageRuleLimitDTO> { }

export interface ISessionPayerAllocation extends INumberIdEntity {
  cdrId?: string | null;
  sessionId?: string | null;
  dimension: SessionAllocationDimensionValues;
  payerType: SessionAllocationPayerTypeValues;
  payerUserId?: string | null;
  payerCompanyId?: number | null;
  amount: number;
  currency: string;
  pricingPolicyId?: number | null;
  sponsorshipPolicyId?: number | null;
  notes?: string | null;
  cdr?: IStringIdEntity;
  session?: IStringIdEntity;
  payerCompany?: ICompany;
  sponsorshipPolicy?: ISponsorshipPolicy;
}

export interface ICreateSessionPayerAllocationDTO {
  cdrId?: string | null;
  sessionId?: string | null;
  dimension: SessionAllocationDimensionValues;
  payerType: SessionAllocationPayerTypeValues;
  payerUserId?: string | null;
  payerCompanyId?: number | null;
  amount: number;
  currency: string;
  pricingPolicyId?: number | null;
  sponsorshipPolicyId?: number | null;
  notes?: string | null;
}

export interface IUpdateSessionPayerAllocationDTO extends Partial<ICreateSessionPayerAllocationDTO> { }

export interface ISessionPayerAllocationSummary {
  dimension: SessionAllocationDimensionValues;
  payerType: SessionAllocationPayerTypeValues;
  payerUserId?: string | null;
  payerCompanyId?: number | null;
  amount: number;
  currency: string;
}

export interface ISessionPayerAllocationDateRange {
  startTime: Date | string;
  endTime: Date | string;
}

export interface ISessionPayerAllocationFilter {
  pageNumber: number;
  searchCriteria: string;
  pageSize?: number;
  /** Sponsor company = company that owns the sponsorship policy (sponsorshipPolicy.companyId) */
  sponsorCompanyId?: number | null;
  /** Session user (matched via session.user or cdr.session.user) */
  sessionUserId?: string | null;
  sponsorshipPolicyId?: number | null;
  dateRange?: ISessionPayerAllocationDateRange | null;
}
